import { useCallback, useEffect, useRef, useState } from 'react'
import { supabase } from './supabase'

/**
 * Zwei kleine Haken statt einer Datenbibliothek.
 *
 * Was hier fehlt — Cache, Deduplizierung, Hintergrund-Aktualisierung — fehlt
 * absichtlich. Die Listen sind kurz, die Abfragen laufen gegen RLS und müssen
 * nach jeder Änderung ohnehin neu gelesen werden. Eine Bibliothek würde hier
 * mehr Begriffe einführen als Arbeit abnehmen.
 */

export interface QueryResult<T> {
  data: T | null
  error: string | null
  loading: boolean
  reload: () => void
}

type SupabaseResult<T> = { data: T | null; error: { message: string } | null }

/**
 * Führt eine Supabase-Abfrage aus und hält Ergebnis, Ladezustand und Fehler.
 *
 * `key` steuert, wann neu geladen wird — bewusst ein expliziter String statt
 * eines Abhängigkeitsfelds, damit nicht versehentlich bei jedem Rendern eine
 * neue Funktionsreferenz eine neue Abfrage auslöst.
 */
export function useQuery<T>(
  key: string,
  run: () => PromiseLike<SupabaseResult<T>>,
): QueryResult<T> {
  const [data, setData] = useState<T | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [nonce, setNonce] = useState(0)

  // Die jeweils aktuelle Fassung, ohne sie zur Abhängigkeit zu machen.
  const runRef = useRef(run)
  runRef.current = run

  // Verhindert, dass eine überholte Antwort eine neuere überschreibt.
  const requestId = useRef(0)

  useEffect(() => {
    const id = ++requestId.current
    setLoading(true)

    Promise.resolve(runRef.current()).then(
      (result) => {
        if (id !== requestId.current) return
        if (result.error) {
          setError(result.error.message)
          setData(null)
        } else {
          setData(result.data)
          setError(null)
        }
        setLoading(false)
      },
      (err: unknown) => {
        if (id !== requestId.current) return
        setError(err instanceof Error ? err.message : 'Unbekannter Fehler')
        setData(null)
        setLoading(false)
      },
    )
  }, [key, nonce])

  const reload = useCallback(() => setNonce((n) => n + 1), [])

  return { data, error, loading, reload }
}

export interface ActionState {
  run: (fn: () => PromiseLike<{ error: { message: string } | null }>) => Promise<boolean>
  busy: boolean
  error: string | null
  clearError: () => void
}

/**
 * Für schreibende Aufrufe: hält Ladezustand und Fehlermeldung.
 *
 * Übersetzt die beiden Fehler, die Nutzer hier am häufigsten sehen, in
 * verständliche Sätze. Ein rohes "new row violates row-level security policy"
 * ist für die betroffene Person wertlos — sie erfährt daraus nicht, dass ihr
 * schlicht ein Recht fehlt.
 */
export function useAction(): ActionState {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const run = useCallback(
    async (fn: () => PromiseLike<{ error: { message: string } | null }>) => {
      setBusy(true)
      setError(null)
      try {
        const { error: actionError } = await fn()
        if (actionError) {
          setError(translate(actionError.message))
          return false
        }
        return true
      } catch (err) {
        setError(err instanceof Error ? translate(err.message) : 'Unbekannter Fehler')
        return false
      } finally {
        setBusy(false)
      }
    },
    [],
  )

  return { run, busy, error, clearError: () => setError(null) }
}

function translate(message: string): string {
  const lower = message.toLowerCase()

  if (lower.includes('row-level security') || lower.includes('42501')) {
    return 'Dafür fehlt Ihnen das nötige Recht in diesem Bereich.'
  }
  if (lower.includes('duplicate key') || lower.includes('unique constraint')) {
    return 'Ein Eintrag mit dieser Kennung existiert in diesem Bereich bereits.'
  }
  if (lower.includes('violates check constraint')) {
    return 'Ein Wert liegt außerhalb des zulässigen Bereichs.'
  }
  return message
}

/** Bereiche, in denen der Nutzer ein bestimmtes Recht hat — für Auswahllisten. */
export function spacesWith(
  spaces: { id: string; name: string; permissions: string[] }[],
  globalPermissions: ReadonlySet<string>,
  permission: string,
) {
  // Ein globales Recht gilt überall — dann stehen alle Bereiche offen.
  if (globalPermissions.has(permission)) return spaces
  return spaces.filter((s) => s.permissions.includes(permission))
}

export { supabase }
